import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Brand from "@/components/Brand";

const NAV_ITEMS = [
  { href: "#mission", label: "Mission" },
  { href: "#difference", label: "Why us" },
  { href: "#process", label: "Process" },
  { href: "#reviews", label: "Reviews" },
  { href: "#faq", label: "FAQ" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the drawer is open so the page underneath does not move
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const linkCls = "text-sm tracking-wide text-[#1D1D1F]/80 hover:text-[#0A1930] transition-colors";

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 ${scrolled ? "glass" : "bg-white/0"}`}
        data-testid="site-nav"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-16">
          <Brand size={32} showText={true} asLink={false} testId="brand-link" />
          <nav className="hidden md:flex items-center gap-8">
            {NAV_ITEMS.map((i) => (
              <a key={i.href} href={i.href} className={linkCls} data-testid={`nav-${i.label.toLowerCase()}`}>{i.label}</a>
            ))}
            <a
              href="#booking"
              className="bg-[#0066CC] text-white text-sm rounded-full px-4 py-2 hover:bg-[#0055AA] transition-colors"
              data-testid="nav-cta-book"
            >
              Book free consult
            </a>
            <Link to="/admin" className="text-xs text-slate-400 hover:text-slate-700" data-testid="nav-admin">Admin</Link>
          </nav>
          <button
            className="md:hidden h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-hamburger"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/*
        IMPORTANT: mobile drawer must live OUTSIDE the <header> above.
        The header uses backdrop-filter (.glass), which creates a containing
        block for fixed descendants — so a drawer placed inside would be
        clipped to the header's height. Keep this as a sibling.
      */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-[100] bg-white flex flex-col"
          role="dialog"
          aria-modal="true"
          data-testid="mobile-menu"
        >
          <div className="flex items-center justify-between px-6 h-16 border-b border-slate-200 bg-white">
            <Brand size={28} showText={true} asLink={false} />
            <button
              onClick={() => setOpen(false)}
              className="h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100"
              aria-label="Close menu"
              data-testid="mobile-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto bg-white">
            <div className="p-6 flex flex-col gap-5 text-lg font-display">
              {NAV_ITEMS.map((i) => (
                <a
                  key={i.href}
                  href={i.href}
                  onClick={() => setOpen(false)}
                  className="text-[#0A1930] py-1.5"
                  data-testid={`mobile-nav-${i.label.toLowerCase()}`}
                >
                  {i.label}
                </a>
              ))}
              <a
                href="#booking"
                onClick={() => setOpen(false)}
                className="mt-4 bg-[#0066CC] text-white text-center rounded-full py-3.5 text-base"
                data-testid="mobile-cta-book"
              >
                Book free consult
              </a>
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="text-sm text-slate-500 mt-4"
                data-testid="mobile-admin"
              >
                Admin sign-in
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
